import { useState, useEffect } from "react";
import axios, { CancelToken } from "axios";
import { useIntl } from "react-intl";

import {
  ACCOUNT_APPLICATION_RESET_PASSWORD,
  CLIENT_APPLICATION,
  CLIENT_APPLICATION_ID,
} from "../../../../constants/api";

const makeRequest = async (url, source, method = "get", data = null) => {
  try {
    return await axios.request({
      url,
      method,
      data,
      cancelToken: source.token,
    });
  } catch (error) {
    return error?.response;
  }
};

const useApplication = () => {
  const [source, setSource] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingReset, setLoadingReset] = useState(false);
  const [error, setError] = useState();
  const [alertError, setAlertError] = useState();
  const [applications, setApplications] = useState([]);
  const intl = useIntl();

  useEffect(() => {
    initialRequest();

    return () => source?.cancel("Component unmounted");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialRequest = async () => {
    setApplications(await getApplications());
  };

  const getApplications = async () => {
    const sourceToken = CancelToken.source();
    setSource(sourceToken);
    setLoading(true);
    const response = await makeRequest(CLIENT_APPLICATION, sourceToken);

    if (!response?.data.success) {
      const error = response?.data?.error;

      if (error && error.code !== "ApplicationNotFoundException") {
        setError(error.description);
      }
      setLoading(false);
      return [];
    }

    setLoading(false);
    return response?.data?.result.slice(0, 50);
  };

  const createApplication = async (data) => {
    setSource(CancelToken.source());
    setAlertError();

    const response = await makeRequest(
      CLIENT_APPLICATION,
      source,
      "post",
      data
    );

    if (!response?.data.success) {
      setAlertError(
        response?.data?.error?.description ||
          intl.formatMessage({ id: "errors.somethingWrong" })
      );
      setLoading(false);
      return undefined;
    }

    setApplications((applications) => [
      ...applications,
      {
        application_name: response.data.result?.application_name,
        application_bundle: response.data.result?.application_bundle,
        callback: response.data.result?.callback,
        key: response.data.result?.key,
        notify: response.data.result?.notify,
        plaftorm: response.data.result?.plaftorm,
        platform_id: response.data.result?.platform_id,
        publishable_key: response.data.result?.publishable_key,
      },
    ]);

    return response.data;
  };

  const updateApplication = async (id, data) => {
    setSource(CancelToken.source());
    setAlertError();

    const response = await makeRequest(
      CLIENT_APPLICATION_ID.replace("{id}", id),
      source,
      "put",
      data
    );

    if (!response?.data.success) {
      setAlertError(
        response?.data?.error?.description ||
          intl.formatMessage({ id: "errors.somethingWrong" })
      );
      return response?.data;
    }

    const index = applications.findIndex((item) => item.key === id);
    const newApplications = [...applications];
    newApplications[index] = response.data.result;

    if (response.data.result.shopify !== undefined) {
      newApplications[index].shopify.method_oxxo = response.data.result.shopify
        .method_oxxo
        ? 1
        : 0;
      newApplications[index].shopify.msi = response.data.result.shopify.msi
        ? 1
        : 0;
    }
    setApplications(newApplications);

    return response?.data;
  };

  const deleteApplication = async (id) => {
    setSource(CancelToken.source());
    setAlertError();

    const response = await makeRequest(
      CLIENT_APPLICATION_ID.replace("{id}", id),
      source,
      "delete"
    );

    if (!response?.data.success) {
      setAlertError(
        response?.data?.error?.description ||
          intl.formatMessage({ id: "errors.somethingWrong" })
      );
      return false;
    }

    setApplications(applications.filter((item) => item.key !== id));
    return true;
  };

  const resetSecret = async (key) => {
    setSource(CancelToken.source());
    setLoadingReset(true);
    setAlertError();

    const response = await makeRequest(
      ACCOUNT_APPLICATION_RESET_PASSWORD,
      source,
      "post",
      { key }
    );

    if (!response?.data.success) {
      setAlertError(
        response?.data?.error?.description ||
          intl.formatMessage({ id: "errors.somethingWrong" })
      );
      return undefined;
    }

    setLoadingReset(false);
    return response?.data;
  };

  return {
    applications,
    createApplication,
    updateApplication,
    deleteApplication,
    resetSecret,
    error,
    alertError,
    clearAlertError: () => setAlertError(),
    loading,
    loadingReset,
  };
};

export default useApplication;
